import React, { useCallback, useEffect, useState } from "react";
import CommonSidebar from "../../components/dashboard/CommonSidebar";
import { useAgentManagement } from "../../hooks/management/useAgentManagement";
import { useAgent } from "../../hooks/management/useAgent";
import AgentSearchBar from "../../components/management/AgentSearchBar";
import { useDebounce } from "../../hooks/common/useDebounce";
import { DataTableColumn } from "../../types/dataTable";
import { AgentItem } from "../../types/agent";
import DataTable from "../../components/common/DataTable";
import ConfirmModal from "../../components/dashboard/ConfirmModal";
import { api } from "../../utils/api";
import { convertFileToBase64 } from "../../utils/imageUtils";
import toast from "react-hot-toast";
import { Pencil, Trash, X } from "lucide-react";

export default function AgentManagement() {
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState<AgentItem | null>(null);
  
  // 수정 모달 관련 상태
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [agentToEdit, setAgentToEdit] = useState<AgentItem | null>(null);
  const [editFormData, setEditFormData] = useState({
    agent_title: "",
    child_license: false,
    category: [] as string[],
  });
  
  // 이미지 관련 상태
  const [selectedImage, setSelectedImage] = useState<{
    file: File | null;
    preview: string | null;
    base64: string | null;
  }>({
    file: null,
    preview: null,
    base64: null,
  });

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const {
    agentList,
    totalCount,
    currentPage,
    setCurrentPage,
    categoryOptions,
    isLoading,
    error,
    isEmpty,
    totalPages,
    fetchAgentList,
    fetchCategories,
    fetchAgentSearch,
    deployAgent,
    deployCancelAgent,
    deleteAgent,
    isDeployLoading,
    isDeployCancelLoading,
    isDeleteLoading,
  } = useAgentManagement();

  const { isEditLoading } = useAgent();

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, category, setCurrentPage]);

  useEffect(() => {
    if (debouncedSearchTerm.trim()) {
      fetchAgentSearch(debouncedSearchTerm, category);
    } else {
      fetchAgentList(currentPage, category);
    }
  }, [
    debouncedSearchTerm,
    category,
    currentPage,
    fetchAgentSearch,
    fetchAgentList,
  ]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setCurrentPage(1);
      if (searchTerm.trim()) {
        fetchAgentSearch(searchTerm, category);
      } else {
        fetchAgentList(currentPage, category);
      }
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleCategoryChange = (category: string) => {
    setCategory(category);
  };

  const handleClearAllFilters = () => {
    setSearchTerm("");
    setCategory("");
    setCurrentPage(1);
    fetchAgentList(currentPage, category);
  };

  const handleDeploy = useCallback(
    async (agent: AgentItem) => {
      try {
        const success = await deployAgent(agent.id);

        if (success) {
          toast.success(
            `${agent.agent_title} Agent가 성공적으로 배포되었습니다! 🚀`
          );
          fetchAgentList(currentPage, category);
        } else {
          toast.error("Agent 배포에 실패했습니다.");
        }
      } catch (error) {
        console.error("Agent 배포 중 오류:", error);
        toast.error("Agent 배포 중 오류가 발생했습니다.");
      }
    },
    [deployAgent, currentPage, category, fetchAgentList]
  );

  const handleDeployCancel = useCallback(
    async (agent: AgentItem) => {
      try {
        const success = await deployCancelAgent(agent.id);

        if (success) {
          toast.success(
            `${agent.agent_title} Agent 배포가 성공적으로 취소되었습니다! 📦`
          );
          fetchAgentList(currentPage, category);
        } else {
          toast.error("Agent 배포 취소에 실패했습니다.");
        }
      } catch (error) {
        console.error("Agent 배포 취소 중 오류:", error);
        toast.error("Agent 배포 취소 중 오류가 발생했습니다.");
      }
    },
    [deployCancelAgent, currentPage, category, fetchAgentList]
  );

  const handleDeleteClick = useCallback((agent: AgentItem) => {
    setAgentToDelete(agent);
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!agentToDelete) return;

    try {
      const success = await deleteAgent(agentToDelete.id);

      if (success) {
        toast.success(
          `${agentToDelete.agent_title} Agent가 성공적으로 삭제되었습니다.`
        );
        
        // 삭제 후 데이터 새로고침
        if (debouncedSearchTerm.trim()) {
          fetchAgentSearch(debouncedSearchTerm, category);
        } else {
          fetchAgentList(currentPage, category);
        }
      } else {
        toast.error("Agent 삭제에 실패했습니다.");
      }
    } catch (error) {
      console.error("Agent 삭제 중 오류:", error);
      toast.error("Agent 삭제 중 오류가 발생했습니다.");
    } finally {
      setDeleteModalOpen(false);
      setAgentToDelete(null);
    }
  }, [agentToDelete, deleteAgent, debouncedSearchTerm, category, currentPage, fetchAgentSearch, fetchAgentList]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteModalOpen(false);
    setAgentToDelete(null);
  }, []);

  // 수정
  const handleEditClick = useCallback((agent: AgentItem) => {
    setAgentToEdit(agent);
    setEditFormData({
      agent_title: agent.agent_title,
      child_license: agent.child_license,
      category: agent.category || [],
    });
    setSelectedImage({
      file: null,
      preview: null,
      base64: null,
    });
    setEditModalOpen(true);
  }, []);

  const handleEditCancel = useCallback(() => {
    setEditModalOpen(false);
    setAgentToEdit(null);
    setEditFormData({
      agent_title: "",
      child_license: false,
      category: [],
    });
    setSelectedImage({
      file: null,
      preview: null,
      base64: null,
    });
  }, []);

  const handleEditConfirm = useCallback(async () => {
    if (!agentToEdit) return;
    
    try {
      const requestData: any = {
        id: agentToEdit.id,
        agent_title: editFormData.agent_title,
        child_license: editFormData.child_license,
        hidden: Boolean(agentToEdit.hidden), 
        category: editFormData.category,
      };
      
      if (selectedImage.base64) {
        requestData.b64 = selectedImage.base64;
      } else {
        requestData.image_url = agentToEdit.agent_image;
      }

      const response = await api.post('/admin/v1/agent/update', requestData);

      if (response.data.status) {
        toast.success(`${agentToEdit.agent_title} Agent가 성공적으로 수정되었습니다!`);
        
        if (debouncedSearchTerm.trim()) {
          fetchAgentSearch(debouncedSearchTerm, category);
        } else {
          fetchAgentList(currentPage, category);
        }
        
        setEditModalOpen(false);
        setAgentToEdit(null);
      } else {
        toast.error("Agent 수정에 실패했습니다.");
      }
    } catch (error: any) {
      console.error("Agent 수정 중 오류:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "Agent 수정 중 오류가 발생했습니다.";
      toast.error(errorMessage);
    }
  }, [agentToEdit, editFormData, selectedImage, debouncedSearchTerm, category, currentPage, fetchAgentSearch, fetchAgentList]);

  // 이미지 파일 
  const handleImageChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      if (file.size > 10 * 1024 * 1024) {
        toast.error("이미지 파일 크기는 10MB를 초과할 수 없습니다.");
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast.error("이미지 파일만 업로드 가능합니다.");
        return;
      }

      const base64 = await convertFileToBase64(file, {
        includeDataPrefix: false,
        maxSize: 10 * 1024 * 1024,
      });

      const preview = URL.createObjectURL(file);

      setSelectedImage({
        file,
        preview,
        base64,
      });

      toast.success("이미지가 성공적으로 업로드되었습니다.");
    } catch (error) {
      console.error("이미지 처리 중 오류:", error);
      toast.error("이미지 처리 중 오류가 발생했습니다.");
    }
  }, []);

  // 이미지 제거
  const handleImageRemove = useCallback(() => {
    if (selectedImage.preview) {
      URL.revokeObjectURL(selectedImage.preview);
    }
    setSelectedImage({
      file: null,
      preview: null,
      base64: null,
    });
  }, [selectedImage.preview]);

  const getCategoryLabel = (kind: string) => {
    switch (kind) {
      case "text-modal":
        return "텍스트 기반";
      case "image-modal":
        return "이미지 기반";
      case "gen-image-modal":
        return "이미지 생성";
      default:
        return kind;
    }
  };

  const getCategoryColor = (kind: string) => {
    switch (kind) {
      case "text-modal":
        return "bg-blue-500/20 text-blue-400";
      case "image-modal":
        return "bg-green-500/20 text-green-400";
      case "gen-image-modal":
        return "bg-purple-500/20 text-purple-400";
      default:
        return "bg-gray-500/20 text-gray-400";
    }
  };

  const getLicenseLabel = (license: string | boolean) => {
    const isChildLicense = license === true || license === "true";
    return isChildLicense ? "아동용" : "성인용";
  };

  const getLicenseColor = (license: string | boolean) => {
    const isChildLicense = license === true || license === "true";
    return isChildLicense
      ? "bg-yellow-500/20 text-yellow-400"
      : "bg-red-500/20 text-red-400";
  };

  const getDeployStatusLabel = (hidden: boolean) => {
    return hidden ? "배포 안됨" : "배포됨";
  };

  const getDeployStatusColor = (hidden: boolean) => {
    return hidden
      ? "bg-gray-500/20 text-gray-400"
      : "bg-green-500/20 text-green-400";
  };

  const columns: DataTableColumn<AgentItem>[] = [
    {
      key: "agent_image",
      header: "이미지",
      render: (value) => (
        <div className="flex items-center">
          <img src={value} alt="agent_image" className="w-14 h-14 rounded-lg" />
        </div>
      ),
    },
    {
      key: "agent_title",
      header: "제목",
      render: (value) => (
        <div className="max-w-xs">
          <h3 className="text-white font-medium truncate">{value}</h3>
        </div>
      ),
    },
    {
      key: "agent_kind",
      header: "카테고리",
      render: (value) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(
            value
          )}`}
        >
          {getCategoryLabel(value)}
        </span>
      ),
    },
    {
      key: "child_license",
      header: "라이선스",
      render: (value) => (
        <div className="max-w-xs">
          <span
            className={`font-medium truncate px-2.5 py-0.5 rounded-full text-xs ${getLicenseColor(
              value
            )}`}
          >
            {getLicenseLabel(value)}
          </span>
        </div>
      ),
    },
    {
      key: "hidden",
      header: "배포 상태",
      render: (value) => (
        <div className="max-w-xs">
          <span
            className={`font-medium truncate px-2.5 py-0.5 rounded-full text-xs ${getDeployStatusColor(
              value
            )}`}
          >
            {getDeployStatusLabel(value)}
          </span>
        </div>
      ),
    },
    {
      key: "agent_id",
      header: "ID",
      render: (value) => (
        <div className="max-w-xs">
          <span className="text-white font-medium truncate">{value}</span>
        </div>
      ),
    },
    {
      key: "actions",
      header: "관리",
      render: (value, item) => {
        return (
          <div className="flex items-center gap-1.5">
            {/* 배포 버튼 - hidden이 true일 때만 표시 */}
            {item.hidden && (
              <button
                onClick={() => handleDeploy(item)}
                disabled={isDeployLoading}
                className="group flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 disabled:bg-slate-600 text-white px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 hover:shadow-lg hover:shadow-green-600/25 disabled:opacity-50"
                title="Agent를 배포합니다"
              >
                {isDeployLoading ? (
                  <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent"></div>
                ) : (
                  <>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    배포
                  </>
                )}
              </button>
            )}
            
            {/* 배포 취소 버튼 - hidden이 false일 때만 표시 */}
            {!item.hidden && (
              <button
                onClick={() => handleDeployCancel(item)}
                disabled={isDeployCancelLoading}
                className="group flex items-center justify-center gap-1.5 bg-orange-600 hover:bg-orange-700 disabled:bg-slate-600 text-white px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 hover:shadow-lg hover:shadow-orange-600/25 disabled:opacity-50"
                title="Agent 배포를 취소합니다"
              >
                {isDeployCancelLoading ? (
                  <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent"></div>
                ) : (
                  <>
                    <X className="w-3 h-3" />
                    취소
                  </>
                )}
              </button>
            )}
            
            {/* 수정 버튼 - 항상 표시 */}
            <button
              onClick={() => handleEditClick(item)}
              className="group flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 hover:shadow-lg hover:shadow-blue-600/25"
              title="Agent 정보를 수정합니다"
            >
              <Pencil className="w-3 h-3" />
              수정
            </button>
            
            {/* 삭제 버튼 - 항상 표시 */}
            <button
              onClick={() => handleDeleteClick(item)}
              disabled={isDeleteLoading}
              className="group flex items-center justify-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:bg-slate-600 text-white px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 hover:shadow-lg hover:shadow-red-600/25 disabled:opacity-50"
              title="Agent를 삭제합니다"
            >
              {isDeleteLoading ? (
                <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent"></div>
              ) : (
                <>
                  <Trash className="w-3 h-3" />
                  삭제
                </>
              )}
            </button>
          </div>
        );
      },
    },
  ];

  const paginationInfo = {
    currentPage,
    totalPages,
    totalCount,
    startIndex: (currentPage - 1) * 15,
    endIndex: Math.min(currentPage * 15, totalCount),
    onPageChange: (page: number) => {
      setCurrentPage(page);
      if (debouncedSearchTerm.trim()) {
        fetchAgentSearch(debouncedSearchTerm, category);
      } else {
        fetchAgentList(page, category);
      }
    },
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 lg:ml-72">
        <div className="bg-slate-900 border-b border-slate-800 p-6">
          <h1 className="text-2xl font-bold text-white">AI Agent 관리</h1>
        </div>

        <CommonSidebar />

        <div className="p-6">
          <AgentSearchBar
            searchTerm={searchTerm}
            onSearchChange={handleSearch}
            onKeyPress={handleKeyPress}
            onClearSearch={handleClearSearch}
            category={category}
            onCategoryChange={handleCategoryChange}
            onClearAllFilters={handleClearAllFilters}
            categoryOptions={categoryOptions}
            totalCount={totalCount}
            debouncedSearchTerm={debouncedSearchTerm}
          />
          <div
            className={`bg-slate-900 rounded-xl border border-slate-800 overflow-hidden p-6`}
          >
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent"></div>
              <span className="ml-3 text-slate-400">
                데이터를 불러오는 중...
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 lg:ml-72">
        <div className="bg-slate-900 border-b border-slate-800 p-6">
          <h1 className="text-2xl font-bold text-white">AI Agent 관리</h1>
        </div>

        <CommonSidebar />

        <div className="p-6">
          <AgentSearchBar
            searchTerm={searchTerm}
            onSearchChange={handleSearch}
            onKeyPress={handleKeyPress}
            onClearSearch={handleClearSearch}
            category={category}
            onCategoryChange={handleCategoryChange}
            onClearAllFilters={handleClearAllFilters}
            categoryOptions={categoryOptions}
            totalCount={totalCount}
            debouncedSearchTerm={debouncedSearchTerm}
          />

          <DataTable<AgentItem>
            columns={columns}
            data={[]}
            pagination={undefined}
            loading={false}
            error={error}
          />
        </div>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="min-h-screen bg-slate-950 lg:ml-72">
        <div className="bg-slate-900 border-b border-slate-800 p-6">
          <h1 className="text-2xl font-bold text-white">AI Agent 관리</h1>
        </div>

        <CommonSidebar />

        <div className="p-6">
          <AgentSearchBar
            searchTerm={searchTerm}
            onSearchChange={handleSearch}
            onKeyPress={handleKeyPress}
            onClearSearch={handleClearSearch}
            category={category}
            onCategoryChange={handleCategoryChange}
            onClearAllFilters={handleClearAllFilters}
            categoryOptions={categoryOptions}
            totalCount={totalCount}
            debouncedSearchTerm={debouncedSearchTerm}
          />

          <DataTable<AgentItem>
            columns={columns}
            data={agentList}
            pagination={paginationInfo}
            loading={false}
            error={null}
            emptyMessage="등록된 AI Agent가 없습니다."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 lg:ml-72">
      {/* 헤더 */}
      <div className="bg-slate-900 border-b border-slate-800 p-6">
        <h1 className="text-2xl font-bold text-white">AI Agent 관리</h1>
      </div>

      <CommonSidebar />

      {/* 메인 컨텐츠 */}
      <div className="p-6">
        {/* 검색 영역 */}
        <AgentSearchBar
          searchTerm={searchTerm}
          onSearchChange={handleSearch}
          onKeyPress={handleKeyPress}
          onClearSearch={handleClearSearch}
          category={category}
          onCategoryChange={handleCategoryChange}
          onClearAllFilters={handleClearAllFilters}
          categoryOptions={categoryOptions}
          totalCount={totalCount}
          debouncedSearchTerm={debouncedSearchTerm}
        />

        <DataTable<AgentItem>
          columns={columns}
          data={agentList}
          pagination={paginationInfo}
          loading={isLoading}
          error={error}
        />
      </div>

      {/* 삭제 확인 모달 */}
      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Agent 삭제 확인"
        message={`"${agentToDelete?.agent_title}" Agent를 정말 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다.`}
        confirmText="삭제"
        cancelText="취소"
        type="danger"
        isLoading={isDeleteLoading}
      />

      {/* 수정 모달 */}
      {editModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto custom-scrollbar">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleEditCancel} />
          
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-slate-900 rounded-lg shadow-xl w-full max-w-2xl overflow-hidden border border-slate-800">
              <div className="flex items-center justify-between p-6 border-b border-slate-800">
                <h2 className="text-xl font-semibold text-white">Agent 수정</h2>
                <button
                  onClick={handleEditCancel}
                  className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                <div className="space-y-6">
                   <div>
                     <label className="block text-sm font-medium text-slate-300 mb-2">
                       Agent 제목
                     </label>
                     <input
                       type="text"
                       value={editFormData.agent_title}
                       onChange={(e) => setEditFormData(prev => ({ ...prev, agent_title: e.target.value }))}
                       className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                       placeholder="Agent 제목을 입력하세요"
                     />
                   </div>

                   <div>
                     <label className="block text-sm font-medium text-slate-300 mb-3">
                       Agent 이미지
                     </label>
                     <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
                       <div className="mb-4">
                         <p className="text-xs text-slate-400 mb-2">현재 이미지:</p>
                         <div className="w-24 h-24 bg-slate-700 rounded-lg overflow-hidden">
                           <img
                             src={agentToEdit?.agent_image}
                             alt="현재 Agent 이미지"
                             className="w-full h-full object-cover"
                             onError={(e) => {
                               e.currentTarget.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23374151'/%3E%3Ctext x='50' y='50' text-anchor='middle' fill='%239CA3AF' font-family='sans-serif' font-size='12'%3E이미지 없음%3C/text%3E%3C/svg%3E";
                             }}
                           />
                         </div>
                       </div>

                       <div className="space-y-3">
                         <p className="text-xs text-slate-400">새 이미지 업로드:</p>
                         <input
                           type="file"
                           accept="image/*"
                           onChange={handleImageChange}
                           className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-600 file:text-white hover:file:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                         />
                         <p className="text-xs text-slate-500">
                           JPG, PNG, GIF 등의 이미지 파일을 선택해주세요. (최대 10MB)
                         </p>
                       </div>

                       {selectedImage.preview && (
                         <div className="mt-4 pt-4 border-t border-slate-700">
                           <div className="flex items-start gap-3">
                             <div className="w-24 h-24 bg-slate-700 rounded-lg overflow-hidden">
                               <img
                                 src={selectedImage.preview}
                                 alt="새 이미지 미리보기"
                                 className="w-full h-full object-cover"
                               />
                             </div>
                             <div className="flex-1 min-w-0">
                               <p className="text-white text-sm font-medium truncate">
                                 {selectedImage.file?.name}
                               </p>
                               <p className="text-slate-400 text-xs">
                                 {selectedImage.file && (selectedImage.file.size / 1024).toFixed(1)} KB
                               </p>
                               <button
                                 type="button"
                                 onClick={handleImageRemove}
                                 className="mt-2 px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-colors"
                               >
                                 제거
                               </button>
                             </div>
                           </div>
                         </div>
                       )}
                     </div>
                   </div>

                   <div>
                     <label className="block text-sm font-medium text-slate-300 mb-3">
                       카테고리 선택
                     </label>
                     <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
                       <div className="space-y-3 max-h-40 overflow-y-auto custom-scrollbar">
                         {categoryOptions.length > 0 ? (
                           categoryOptions.map((cat) => (
                             <label key={cat.value} className="flex items-center space-x-3 cursor-pointer group hover:bg-slate-700/50 rounded-lg p-2 transition-colors">
                               <input
                                 type="checkbox"
                                 checked={editFormData.category.includes(cat.value)}
                                 onChange={(e) => {
                                   if (e.target.checked) {
                                     setEditFormData(prev => ({
                                       ...prev,
                                       category: [...prev.category, cat.value]
                                     }));
                                   } else {
                                     setEditFormData(prev => ({
                                       ...prev,
                                       category: prev.category.filter(c => c !== cat.value)
                                     }));
                                   }
                                 }}
                                 className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500 focus:ring-2"
                               />
                               <span className="text-slate-300 text-sm group-hover:text-white transition-colors">{cat.label}</span>
                             </label>
                           ))
                         ) : (
                           <div className="text-center py-4">
                             <p className="text-slate-500 text-sm">사용 가능한 카테고리가 없습니다.</p>
                           </div>
                         )}
                       </div>
                       {editFormData.category.length > 0 && (
                         <div className="mt-3 pt-3 border-t border-slate-700">
                           <p className="text-xs text-slate-400 mb-2">선택된 카테고리:</p>
                           <div className="flex flex-wrap gap-2">
                             {editFormData.category.map((catValue) => {
                               const cat = categoryOptions.find(c => c.value === catValue);
                               return (
                                 <span
                                   key={catValue}
                                   className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-600/20 text-blue-400 border border-blue-600/30"
                                 >
                                   {cat?.label || catValue}
                                 </span>
                               );
                             })}
                           </div>
                         </div>
                       )}
                     </div>
                   </div>

                   <div className="space-y-4">
                     <h3 className="text-sm font-medium text-slate-300 mb-3">추가 설정</h3>
                     
                     <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
                       <label className="flex items-start space-x-3 cursor-pointer group">
                         <input
                           type="checkbox"
                           checked={editFormData.child_license}
                           onChange={(e) => setEditFormData(prev => ({ ...prev, child_license: e.target.checked }))}
                           className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500 focus:ring-2 mt-0.5"
                         />
                         <div className="flex-1">
                           <span className="text-slate-300 text-sm font-medium group-hover:text-white transition-colors">
                             아동 라이선스 적용
                           </span>
                           <p className="text-xs text-slate-500 mt-1">
                             아동을 대상으로 하는 Agent인 경우 체크해주세요.
                           </p>
                         </div>
                       </label>
                     </div>

                   </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-800">
                <button
                  onClick={handleEditCancel}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
                >
                  취소
                </button>
                <button
                  onClick={handleEditConfirm}
                  disabled={isEditLoading}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white rounded-lg font-medium transition-colors"
                >
                  {isEditLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      수정 중...
                    </div>
                  ) : (
                    "수정"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
